import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Download, Sparkles, Pencil, Save, CheckCircle2, Circle, FileText, ExternalLink, Trash2 } from "lucide-react";

const CHAPTERS = [
  { key: "abstrak", label: "Abstrak", short: "Abstrak" },
  { key: "bab1", label: "BAB I Pendahuluan", short: "Bab 1" },
  { key: "bab2", label: "BAB II Tinjauan Pustaka", short: "Bab 2" },
  { key: "bab3", label: "BAB III Metodologi", short: "Bab 3" },
  { key: "bab4", label: "BAB IV Hasil & Pembahasan", short: "Bab 4" },
  { key: "bab5", label: "BAB V Penutup", short: "Bab 5" },
  { key: "daftar_pustaka", label: "Daftar Pustaka", short: "Pustaka" },
];

// small markdown renderer (preserves headings, bold, lists)
function md(text = "") {
  const out = [];
  const lines = text.split("\n");
  lines.forEach((line, i) => {
    if (line.startsWith("# ")) out.push(<h2 key={i} className="serif text-2xl font-bold text-[#0F2922] mt-4 mb-2">{line.slice(2)}</h2>);
    else if (line.startsWith("## ")) out.push(<h3 key={i} className="serif text-xl font-semibold text-[#0F2922] mt-4 mb-2">{line.slice(3)}</h3>);
    else if (line.startsWith("### ")) out.push(<h4 key={i} className="font-semibold text-[#0F2922] mt-3 mb-1">{line.slice(4)}</h4>);
    else if (/^\s*[-*] /.test(line)) out.push(<li key={i} className="ml-6 list-disc leading-relaxed">{formatInline(line.replace(/^\s*[-*] /, ""))}</li>);
    else if (/^\s*\d+\.\s/.test(line)) out.push(<li key={i} className="ml-6 list-decimal leading-relaxed">{formatInline(line.replace(/^\s*\d+\.\s/, ""))}</li>);
    else if (line.trim() === "") out.push(<div key={i} className="h-2"/>);
    else out.push(<p key={i} className="leading-relaxed text-slate-800 mt-1">{formatInline(line)}</p>);
  });
  return out;
}
function formatInline(t) {
  return t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : p);
}

export default function ThesisDraft() {
  const { id } = useParams();
  const nav = useNavigate();
  const [draft, setDraft] = useState(null);
  const [active, setActive] = useState("abstrak");
  const [editing, setEditing] = useState(false);
  const [editBuf, setEditBuf] = useState("");
  const [busyKey, setBusyKey] = useState(null); // chapter being generated
  const [saving, setSaving] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("kt_token") : "";
  const pollRef = useRef({});

  const load = async () => {
    const { data } = await http.get(`/thesis-drafts/${id}`);
    setDraft(data);
    // auto-select first empty chapter
    const first = CHAPTERS.find(c => !(data.chapters || {})[c.key]);
    if (first && !editing) setActive(first.key);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const generateChapter = async (key) => {
    setBusyKey(key);
    try {
      const { data: job } = await http.post(`/thesis-drafts/${id}/generate-chapter`, { chapter_key: key });
      const jobId = job.job_id;
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: j } = await http.get(`/ai/job/${jobId}`);
        if (j.status === "done") { await load(); toast.success(`${CHAPTERS.find(c=>c.key===key)?.short} selesai ditulis!`); return; }
        if (j.status === "error") throw new Error(j.error || "AI error");
      }
      throw new Error("Timeout");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "AI gagal"); }
    finally { setBusyKey(null); }
  };

  const startEdit = () => {
    setEditBuf(draft?.chapters?.[active] || ""); setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await http.patch(`/thesis-drafts/${id}/chapter`, { chapter_key: active, content: editBuf });
      setDraft(data); setEditing(false); toast.success("Perubahan tersimpan");
    } catch (e) { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const removeDraft = async () => {
    if (!confirm("Hapus seluruh draft skripsi ini? Tindakan tidak dapat dibatalkan.")) return;
    await http.delete(`/thesis-drafts/${id}`);
    toast.success("Draft dihapus"); nav("/tugas-akhir");
  };

  if (!draft) return <div className="text-slate-400 italic">Memuat draft…</div>;

  const chapters = draft.chapters || {};
  const done = CHAPTERS.filter(c => (chapters[c.key] || "").trim()).length;
  const pct = Math.round((done / CHAPTERS.length) * 100);
  const activeContent = chapters[active];
  const activeLabel = CHAPTERS.find(c => c.key === active)?.label;
  const wordCount = (activeContent || "").split(/\s+/).filter(Boolean).length;
  const exportUrl = `${API}/thesis-drafts/${id}/export/docx?auth=${encodeURIComponent(token || "")}`;

  return (
    <div data-testid="thesis-draft-page">
      <Link to="/tugas-akhir" className="inline-flex items-center gap-1 text-sm text-[#D97706] font-semibold"><ArrowLeft size={14}/> Kembali ke ide TA</Link>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <Badge className="bg-[#0F2922] text-[#FAFA96]">{draft.jenjang}</Badge>
        <Badge variant="outline">{draft.field || "-"}</Badge>
      </div>
      <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1 leading-tight">{draft.selected_title}</h1>

      <div className="kt-card p-5 mt-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center justify-between text-sm mb-2">
            <b>Progres penulisan</b>
            <span className="font-semibold text-[#0F2922]">{done}/{CHAPTERS.length} bab · {pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D97706] to-amber-500 transition-all" style={{ width: `${pct}%` }}/>
          </div>
        </div>
        <a href={exportUrl} target="_blank" rel="noreferrer">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={done === 0} data-testid="thesis-export-docx">
            <Download size={14} className="mr-2"/> Unduh .docx (Google Docs)
          </Button>
        </a>
        <Button variant="outline" onClick={removeDraft} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="thesis-delete-draft">
          <Trash2 size={14} className="mr-1"/> Hapus
        </Button>
      </div>

      <div className="grid lg:grid-cols-4 gap-5 mt-5">
        {/* Left: chapter list */}
        <aside className="kt-card p-3 lg:sticky lg:top-24 h-fit">
          <div className="text-xs uppercase tracking-widest text-slate-500 px-2 py-1">Struktur</div>
          <div className="space-y-1 mt-1">
            {CHAPTERS.map((c) => {
              const filled = !!(chapters[c.key] || "").trim();
              const isActive = c.key === active;
              return (
                <button
                  key={c.key}
                  onClick={() => { setActive(c.key); setEditing(false); }}
                  data-testid={`chapter-tab-${c.key}`}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition ${isActive ? "bg-[#0F2922] text-[#FAFA96]" : "hover:bg-slate-100 text-slate-700"}`}
                >
                  {filled ? <CheckCircle2 size={14} className={isActive ? "text-[#FAFA96]" : "text-emerald-600"}/> : <Circle size={14} className="text-slate-400"/>}
                  <span className="flex-1">{c.short}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 p-3 rounded-lg bg-[#FAFA96]/40 border border-[#D97706]/20 text-[11px] text-slate-600">
            <b className="text-[#0F2922]">Tips:</b> DOCX yang diunduh langsung bisa dibuka di <b>Google Docs</b> (Drive → Buka dengan → Google Docs).
          </div>
        </aside>

        {/* Right: chapter content */}
        <section className="lg:col-span-3 kt-card p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">Sedang dilihat</div>
              <h2 className="serif text-xl font-bold text-[#0F2922]">{activeLabel}</h2>
              {activeContent && !editing && <div className="text-xs text-slate-500 mt-1">{wordCount.toLocaleString("id-ID")} kata</div>}
            </div>
            <div className="flex items-center gap-2">
              {!activeContent && !editing && (
                <Button onClick={() => generateChapter(active)} disabled={busyKey === active} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="chapter-generate-btn">
                  {busyKey === active ? <><Loader2 className="mr-2 animate-spin" size={14}/> Menulis…</> : <><Sparkles size={14} className="mr-2"/> Tulis dengan AI</>}
                </Button>
              )}
              {activeContent && !editing && (
                <>
                  <Button variant="outline" onClick={() => generateChapter(active)} disabled={busyKey === active} data-testid="chapter-regenerate-btn">
                    {busyKey === active ? <Loader2 className="animate-spin" size={14}/> : <><Sparkles size={14} className="mr-1"/> Regenerate</>}
                  </Button>
                  <Button onClick={startEdit} variant="outline" data-testid="chapter-edit-btn"><Pencil size={14} className="mr-1"/> Edit</Button>
                </>
              )}
              {editing && (
                <>
                  <Button onClick={() => setEditing(false)} variant="outline">Batal</Button>
                  <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="chapter-save-btn">
                    {saving ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14} className="mr-1"/> Simpan</>}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-5">
            {busyKey === active && !activeContent && (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0F2922] text-[#FAFA96] flex items-center justify-center animate-pulse"><Sparkles size={24}/></div>
                <p className="mt-3 text-slate-600">Claude Sonnet 5 sedang menyusun <b>{activeLabel}</b>… biasanya 30-60 detik.</p>
              </div>
            )}
            {!activeContent && !editing && busyKey !== active && (
              <div className="text-center py-16">
                <FileText size={44} className="mx-auto text-slate-300" strokeWidth={1.2}/>
                <p className="mt-3 text-slate-500">Bab ini masih kosong. Klik <b>Tulis dengan AI</b> untuk memulai draft otomatis, atau langsung <button className="underline text-[#D97706]" onClick={startEdit}>tulis manual</button>.</p>
              </div>
            )}
            {editing && (
              <div>
                <Textarea value={editBuf} onChange={e => setEditBuf(e.target.value)} rows={22} className="font-mono text-sm leading-relaxed" placeholder="Tulis atau tempel isi bab di sini. Markdown didukung (# heading, ## sub, - bullet, **bold**)." data-testid="chapter-edit-area"/>
                <div className="text-[11px] text-slate-500 mt-1">Markdown: <b># Judul</b>, <b>## Sub</b>, <b>- bullet</b>, <b>1. list</b>, <b>**bold**</b></div>
              </div>
            )}
            {activeContent && !editing && (
              <article className="prose max-w-none">{md(activeContent)}</article>
            )}
          </div>
        </section>
      </div>

      {done > 0 && (
        <div className="mt-6 rounded-2xl p-6 bg-gradient-to-br from-[#0F2922] via-[#173E34] to-[#0F2922] text-[#FAFA96] flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest opacity-70">Siap disidangkan?</div>
            <div className="serif text-xl font-bold mt-1">Ekspor draft lengkap ke Google Docs</div>
            <div className="text-sm opacity-80 mt-1">Unduh sebagai .docx, unggah ke Google Drive, klik kanan → <b>Buka dengan Google Docs</b>. Lanjutkan revisi bersama dosen pembimbing di sana.</div>
          </div>
          <a href={exportUrl} target="_blank" rel="noreferrer">
            <Button className="bg-[#D97706] hover:bg-[#B45309] text-white h-11 px-6" disabled={done === 0}>
              <Download size={16} className="mr-2"/> Unduh .docx
            </Button>
          </a>
          <a href="https://drive.google.com/drive/my-drive" target="_blank" rel="noreferrer" className="text-[#FAFA96] text-xs underline inline-flex items-center gap-1">Buka Google Drive <ExternalLink size={11}/></a>
        </div>
      )}
    </div>
  );
}
